/**
 * Coordinates in-flight gameplay writes with auth identity transitions.
 *
 * A lease is the synchronous dispatch boundary: once acquired, the request
 * may finish under the identity it captured. Auth events close the barrier so
 * queued work cannot acquire a lease after the JWT has started resolving.
 */
export type PersistenceWriteLease = {
  dispatchToken: number;
  release: () => void;
};

let acceptingLeases = true;
let nextDispatchToken = 0;
const inFlight = new Set<Promise<void>>();

export const blockActiveRunPersistenceWrites = (): void => {
  acceptingLeases = false;
};

export const allowActiveRunPersistenceWrites = (): void => {
  acceptingLeases = true;
};

export const acquireActiveRunPersistenceWrite = (): PersistenceWriteLease | null => {
  if (!acceptingLeases) return null;

  let resolveLease!: () => void;
  const completion = new Promise<void>((resolve) => {
    resolveLease = resolve;
  });
  inFlight.add(completion);
  let released = false;

  return {
    dispatchToken: ++nextDispatchToken,
    release: () => {
      if (released) return;
      released = true;
      inFlight.delete(completion);
      resolveLease();
    },
  };
};

/** Waits only for writes that were already dispatched when the barrier closed. */
export const waitForActiveRunPersistenceWrites = async (): Promise<void> => {
  await Promise.all([...inFlight]);
};

export const hasActiveRunPersistenceWrites = (): boolean => inFlight.size > 0;

/** Test/runtime reset; active identity remains responsible for readiness. */
export const resetActiveRunPersistenceWrites = (): void => {
  acceptingLeases = true;
  nextDispatchToken = 0;
  inFlight.clear();
};
